import React, { useCallback, useState } from 'react'
import { localizedStrings } from '@core/strings'
import { MenuItem, TextField } from '@common/material'
import { IUser, User } from '@core/entities/user'
import { useGetRegisterUsers } from '@core/react-query/features/registers/registers/register/hooks'
import { Autocomplete } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import _debounce from 'lodash/debounce'
import { Box } from '@fundamentals'
import { TablePrimaryText, TableSecondaryText } from '@common/Table'
import { RegisterItemPropertyFormItemProps } from '@modules/registers/items/form/types'
import { RegisterItemUserProperty } from '@core/entities/register/RegisterItem'
import { useToast } from '@core/toast'

export const RegisterItemUserPropertyFormItem: React.FC<
  RegisterItemPropertyFormItemProps
> = ({ property, register, disabled, registerItem, sx }) => {
  const { control } = useFormContext()
  const toast = useToast()
  const PAGE_SIZE = 10
  const [term, setTerm] = useState<string>('')

  const initialUser = (
    registerItem?.data.find(
      (item) => item.registerPropertyId === String(property.id),
    ) as RegisterItemUserProperty<string>
  )?.selectedUser
  // This cache contains all the users that this component has seen.This allows us to show
  // the selected user even if the response from the back-end doesn't contain a user due to the search term.
  const [userCache, setUserCache] = useState<Record<string, IUser>>({
    [initialUser?.id]: initialUser,
  })

  const { data: registerUsersPage, isFetching } = useGetRegisterUsers(
    {
      registerId: register?.id,
      registerPropertyId: property.id,
      size: PAGE_SIZE,
      term,
    },
    {
      enabled: !!register?.id && !!property.id,
      onError: (e) => toast.error(e?.message),
      onSuccess: (data) => {
        setUserCache((prev) => ({
          ...prev,
          ...data?.content.reduce((acc, user) => {
            acc[user.id] = user
            return acc
          }, {}),
        }))
      },
    },
  )

  const registerUsers = registerUsersPage?.content ?? []
  const isDisabled =
    disabled || (!!registerItem && property.lockedAfterCreation)

  const debouncedSearch = useCallback(
    _debounce((term) => setTerm(term), 300),
    [],
  )

  return (
    <Controller
      control={control}
      name={`${property.id}`}
      rules={{
        validate: (value) => !!value?.selectedUserId || !property.required,
      }}
      render={({ field: { onChange, value }, formState: { errors } }) => (
        <Autocomplete
          value={value?.selectedUserId || ''}
          options={registerUsers}
          disabled={isDisabled}
          loading={isFetching}
          data-test={`${property.type}-${property.name}`}
          filterOptions={(options) => options}
          onChange={(_, value: User) => {
            onChange({
              ...property,
              registerPropertyId: property.id,
              selectedUserId: value ? value.id : null,
            })
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              InputLabelProps={{ shrink: true }}
              placeholder={localizedStrings.typeOrSelect}
              label={property.name}
              required={property.required}
              onChange={(e) => debouncedSearch(e.target.value)}
              error={!!Object.keys(errors).includes(`${property.id}`)}
              sx={{ mb: 2, ...sx }}
            />
          )}
          getOptionLabel={() => userCache[value?.selectedUserId]?.email || ''}
          renderOption={(props, user: User) => (
            <MenuItem key={user.id} value={user.id} {...props}>
              <Box>
                <TablePrimaryText>{user.getFullName()}</TablePrimaryText>
                <TableSecondaryText>{user?.email}</TableSecondaryText>
              </Box>
            </MenuItem>
          )}
        />
      )}
    />
  )
}
